// ═══════════════════════════════════════════════════════════════════════════
// PAGE: SYNTHÈSE D'UN PROJET
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Vue filtrée sur un projet : actions/état, ratio acteurs individuels,
// indicateurs de la révision courante (carte, août 2026 — remplace le KPI
// « nombre de révisions »), risques/sévérité, users du projet groupés
// par équipe.

interface ActionStatusFromApi {
  name: string;
  color?: string | null;
}

interface RiskColorRow {
  severity: number;
  color: string;
}

interface StatsRefData {
  statusByName: Record<string, ActionStatusFromApi>;
  riskColorBySeverity: Record<number, string>;
}

interface DonutItem {
  label: string;
  count: number;
}

interface StackedAgePayload {
  buckets: string[];
  statuses: Array<{ name: string; counts: number[] }>;
}

/** Les indicateurs de la révision courante du projet — ceux que calculait la
 *  barre de statistiques de l'écran de révision, qui n'était plus rendue. */
interface CurrentRevisionKpis {
  revision_id: number;
  revision_name: string;
  is_closed: boolean;
  risks_evaluated: number;
  /** Sévérité = probabilité × impact, sur les seuls risques pertinents. */
  severity_max: number;
  risks_critical: number;
  actions_total: number;
}

interface ProjectSynthesisData {
  actions_by_status?: DonutItem[];
  actions_with_owner?: { with_owner?: number; without_owner?: number };
  actions_with_individual_actor?: { with_individual?: number; without_individual?: number };
  revisions_total?: number;
  /** `null` quand le projet n'a aucune révision. */
  current_revision?: CurrentRevisionKpis | null;
  actions_by_status_and_age?: StackedAgePayload;
  users_by_team?: DonutItem[];
  // RefactoUX mai 2026 : nb d'utilisateurs ayant accès au projet par niveau
  // de droit objet (DC/DM/DD/DS). Donut affiché à côté de la qualité du RACI.
  users_by_rights?: DonutItem[];
}

interface ProjectSummary {
  id: number;
  code?: string | null;
  name: string;
}

declare global {
  interface Window {
    _loadStatsRefData: typeof _loadStatsRefData;
    _SEVERITY_REPRESENTATIVE: typeof _SEVERITY_REPRESENTATIVE;
    _colorForStatus: typeof _colorForStatus;
    _colorForSeverityBin: typeof _colorForSeverityBin;
    _loadProjectSynthesis: typeof _loadProjectSynthesis;
    _renderStackedAgeChart: typeof _renderStackedAgeChart;
    _renderRaciQualityChart: typeof _renderRaciQualityChart;
    _renderDonutColored: typeof _renderDonutColored;
  }
}

let _statsRefData: StatsRefData | null = null;

async function _loadStatsRefData(): Promise<StatsRefData> {
  if (_statsRefData) return _statsRefData;
  const [statuses, riskColors] = (await Promise.all([
    api.getActionStatuses().catch((e: unknown) => {
      safeWarn('getActionStatuses (stats)', e);
      return [];
    }),
    api.getRiskColors().catch((e: unknown) => {
      safeWarn('getRiskColors (stats)', e);
      return [];
    }),
  ])) as [ActionStatusFromApi[], RiskColorRow[]];
  const statusByName: Record<string, ActionStatusFromApi> = {};
  for (const s of statuses || []) statusByName[s.name] = s;
  const riskColorBySeverity: Record<number, string> = {};
  for (const r of riskColors || []) riskColorBySeverity[r.severity] = r.color;
  _statsRefData = { statusByName, riskColorBySeverity };
  return _statsRefData;
}

const _SEVERITY_REPRESENTATIVE: Record<string, number | null> = {
  Critique: 9,
  Élevé: 6,
  Modéré: 4,
  Faible: 2,
  'Non évalué': null,
};

function _colorForStatus(label: string, ref: StatsRefData): string | null {
  return ref.statusByName[label]?.color || null;
}

/** Couleur d'une sévérité chiffrée : la palette admin la plus proche. */
function _colorForSeverity(sev: number, ref: StatsRefData): string | null {
  const keys = Object.keys(ref.riskColorBySeverity)
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  if (!keys.length) return null;
  let best = keys[0] ?? 0;
  for (const k of keys) {
    if (Math.abs(k - sev) < Math.abs(best - sev)) best = k;
  }
  return ref.riskColorBySeverity[best] || null;
}

function _colorForSeverityBin(label: string, ref: StatsRefData): string | null {
  const sev = _SEVERITY_REPRESENTATIVE[label];
  if (sev == null) return '#9ca3af';
  return _colorForSeverity(sev, ref);
}

/** Remplit la carte « Révision courante ». Tout en `textContent` : aucune
 *  donnée serveur n'entre dans du HTML, donc rien à échapper.
 *
 *  Sans révision, les quatre nombres restent à « — » plutôt que d'afficher des
 *  zéros : un projet qui n'a jamais été revu n'a pas zéro risque, il n'a pas
 *  encore de mesure. Un zéro se lirait comme un résultat. */
function _renderCurrentRevision(
  rev: CurrentRevisionKpis | null | undefined,
  ref: StatsRefData,
): void {
  const caption = document.getElementById('ssyn-rev-caption');
  const link = document.getElementById('ssyn-rev-link') as HTMLAnchorElement | null;
  const cells = {
    risks: document.getElementById('ssyn-rev-risks'),
    sevmax: document.getElementById('ssyn-rev-sevmax'),
    critical: document.getElementById('ssyn-rev-critical'),
    actions: document.getElementById('ssyn-rev-actions'),
  };
  if (!rev) {
    if (caption) caption.textContent = t('pageStatsSynthesis.revNone');
    for (const cell of Object.values(cells)) if (cell) cell.textContent = '—';
    if (cells.sevmax) cells.sevmax.style.color = '';
    if (link) link.style.display = 'none';
    return;
  }

  const status = rev.is_closed
    ? t('pageStatsSynthesis.revClosed')
    : t('pageStatsSynthesis.revOpen');
  if (caption) caption.textContent = `${rev.revision_name} · ${status}`;
  if (cells.risks) cells.risks.textContent = String(rev.risks_evaluated);
  if (cells.critical) cells.critical.textContent = String(rev.risks_critical);
  if (cells.actions) cells.actions.textContent = String(rev.actions_total);
  if (cells.sevmax) {
    cells.sevmax.textContent = String(rev.severity_max);
    // Même palette que le donut des sévérités et que la matrice : un 12 doit
    // avoir la même couleur partout, sinon le rapprochement est impossible.
    cells.sevmax.style.color = rev.severity_max
      ? _colorForSeverity(rev.severity_max, ref) || ''
      : '';
  }
  if (link) {
    link.href = `#revision/${rev.revision_id}`;
    link.style.display = '';
  }
}

/** Une ligne de la carte « Révision courante » : un grand nombre, un libellé.
 *
 *  Le nombre est posé plus tard en `textContent` — aucune interpolation de
 *  donnée ici, seulement un identifiant et un libellé traduit. */
function _revStatHtml(id: string, label: string): string {
  return `<div style="text-align:center">
      <div id="${esc(id)}" style="font-size:38px;font-weight:600;line-height:1.05">—</div>
      <div style="font-size:11px;color:var(--text3)">${esc(label)}</div>
    </div>`;
}

pages.statsProjectSynthesis = async function () {
  // Header : titre seul. Le retour vers le dashboard se fait via le breadcrumb
  // affiché en haut de la zone de contenu (`<a href="#dashboard">Projets</a> ›
  // Synthèse d'un projet`).
  setHeader(t('pageStatsSynthesis.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  let projects: ProjectSummary[];
  let ref: StatsRefData;
  try {
    [projects, ref] = (await Promise.all([api.getProjects(), _loadStatsRefData()])) as [
      ProjectSummary[],
      StatsRefData,
    ];
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('pageStatsSynthesis.errLoadProjects');
    setContent(`<div class="error" style="padding:16px">${esc(msg)}</div>`);
    return;
  }

  let selectedPid: string | undefined;
  let lockedDropdown = false;
  try {
    const h = window.location.hash || '';
    const qIdx = h.indexOf('?');
    if (qIdx >= 0) {
      const q = new URLSearchParams(h.slice(qIdx + 1));
      const pid = q.get('proj');
      if (pid) selectedPid = pid;
      if (q.get('locked') === '1') lockedDropdown = true;
    }
  } catch {
    /* noop */
  }
  if (!selectedPid) {
    try {
      selectedPid =
        sessionStorage.getItem('statsProjectId') ||
        (projects[0] && String(projects[0].id)) ||
        'none';
    } catch (e: unknown) {
      safeWarn('sessionStorage.getItem statsProjectId', e);
      selectedPid = (projects[0] && String(projects[0].id)) || 'none';
    }
  }

  const projectOptions = [
    `<option value="none" ${selectedPid === 'none' ? 'selected' : ''}>${esc(t('pageStatsSynthesis.optNoProject'))}</option>`,
    ...projects.map(
      (p) =>
        `<option value="${p.id}" ${String(p.id) === selectedPid ? 'selected' : ''}>[${esc(p.code || '')}] ${esc(p.name)}</option>`,
    ),
  ].join('');

  setContent(`
    <div style="padding:8px">
      <!-- Breadcrumb + sélecteur de projet sur la même ligne -->
      <nav aria-label="Fil d'Ariane" style="font-size:12px;color:var(--text2);margin-bottom:10px">
        <a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('pageStatsSynthesis.breadcrumbProjects'))}</a>
        <span style="color:var(--text3);margin:0 6px">›</span>
        <span style="color:var(--text2);font-weight:500">${esc(t('pageStatsSynthesis.title'))}</span>
      </nav>
      <div style="margin-bottom:12px;display:flex;align-items:center;gap:8px">
        <label style="font-size:12px;color:var(--text3)">${esc(t('pageStatsSynthesis.lblProject'))}</label>
        <select id="stats-synth-project" style="font-size:12px;padding:4px 8px;min-width:280px" ${lockedDropdown ? 'disabled' : ''}>
          ${projectOptions}
        </select>
      </div>

      <!-- Grille 3 cols (mai 2026, MAJ) :
           ligne 1 = Matrice (2/3) + Nb révisions (1/3)
           ligne 2 = Actions par état (1/3) + Qualité RACI (1/3) + Users par droits (1/3)
           ligne 3 = Status × ancienneté (full)
           ligne 4 = Users par équipe (full) -->
      <div id="stats-synth-grid" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px">
        <div class="card" style="padding:12px;grid-column:1 / span 2;grid-row:1">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardRiskMatrix'))}</h3>
          <div id="ssyn-matrix" style="height:520px"></div>
        </div>
        <div class="card" style="padding:12px;grid-column:3;grid-row:1;display:flex;flex-direction:column">
          <h3 style="margin:0;font-size:13px">${esc(t('pageStatsSynthesis.cardCurrentRevision'))}</h3>
          <div id="ssyn-rev-caption" style="font-size:11px;color:var(--text3);margin-top:2px">—</div>
          <div style="flex:1;display:flex;flex-direction:column;justify-content:space-evenly;gap:6px">
            ${_revStatHtml('ssyn-rev-risks', t('pageStatsSynthesis.revRisksEvaluated'))}
            ${_revStatHtml('ssyn-rev-sevmax', t('pageStatsSynthesis.revSeverityMax'))}
            ${_revStatHtml('ssyn-rev-critical', t('pageStatsSynthesis.revRisksCritical'))}
            ${_revStatHtml('ssyn-rev-actions', t('pageStatsSynthesis.revActions'))}
          </div>
          <a id="ssyn-rev-link" href="#" style="display:none;font-size:12px;color:var(--accent);text-decoration:underline;text-align:center">${esc(t('pageStatsSynthesis.revLink'))}</a>
        </div>
        <div class="card" style="padding:12px;grid-column:1">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardActionsByStatus'))}</h3>
          <div id="ssyn-by-status" style="height:280px"></div>
        </div>
        <div class="card" style="padding:12px;grid-column:2">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardRaciQuality'))}</h3>
          <div id="ssyn-raci-quality" style="height:280px"></div>
          <div style="font-size:11px;color:var(--text3);margin-top:6px;text-align:center">
            ${esc(t('pageStatsSynthesis.raciCaption'))}
          </div>
        </div>
        <div class="card" style="padding:12px;grid-column:3">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardUsersByRights'))}</h3>
          <div id="ssyn-by-rights" style="height:280px"></div>
          <div style="font-size:11px;color:var(--text3);margin-top:6px;text-align:center">
            ${esc(t('pageStatsSynthesis.rightsCaption'))}
          </div>
        </div>
        <div class="card" style="padding:12px;grid-column:1 / span 3">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardActionsByStatusAge'))}</h3>
          <div id="ssyn-by-status-and-age" style="height:320px"></div>
        </div>
        <div class="card" style="padding:12px;grid-column:1 / span 3">
          <h3 style="margin:0 0 8px 0;font-size:13px">${esc(t('pageStatsSynthesis.cardUsersByTeam'))}</h3>
          <div id="ssyn-by-team" style="height:300px"></div>
        </div>
      </div>
    </div>
  `);

  document.getElementById('stats-synth-project')?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement | null;
    const pid = target?.value || 'none';
    try {
      sessionStorage.setItem('statsProjectId', pid);
    } catch (err: unknown) {
      safeWarn('sessionStorage.setItem statsProjectId', err);
    }
    void _loadProjectSynthesis(pid, ref);
  });

  // Mode US (mai 2026) : verrouille les 4 blocs non accessibles à l'offre Solo.
  if (window.isSoloUser?.() && window.markLocked) {
    for (const blockId of [
      'ssyn-matrix',
      'ssyn-rev-caption',
      'ssyn-raci-quality',
      'ssyn-by-rights',
    ]) {
      const block = document.getElementById(blockId);
      // On cible la carte parente (.card) pour englober le titre et le contenu.
      const card = block?.closest('.card') as HTMLElement | null;
      if (card) window.markLocked(card, 'solo');
    }
  }

  void _loadProjectSynthesis(selectedPid, ref);
};

async function _loadProjectSynthesis(pid: string, ref: StatsRefData): Promise<void> {
  let data: ProjectSynthesisData;
  try {
    data = (await api.getProjectSynthesis(pid)) as ProjectSynthesisData;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : '';
    safeToast(t('pageStatsSynthesis.errLoadSynthesis') + ' ' + msg, 'error');
    return;
  }

  _renderDonutColored('ssyn-by-status', data.actions_by_status, (item) =>
    _colorForStatus(item.label, ref),
  );

  _renderRaciQualityChart('ssyn-raci-quality', data);

  // Donut "Users par droit objet" (mai 2026) : palette dédiée + libellés
  // étendus (DS → Superviseur, etc.) pour les utilisateurs qui ne connaîtraient
  // pas les acronymes du modèle de droits.
  const _rightsColor = (level: string): string => {
    switch (level) {
      case 'DS':
        return '#0ea5e9'; // bleu — niveau le plus large
      case 'DD':
        return '#147a57'; // accent vert sombre — direction
      case 'DM':
        return '#f59e0b'; // ambre — membre contributif
      case 'DC':
        return '#94a3b8'; // gris — client (rare)
      default:
        return 'var(--text3)';
    }
  };
  const _rightsLabel = (level: string): string => {
    switch (level) {
      case 'DS':
        return t('pageStatsSynthesis.rights.DS');
      case 'DD':
        return t('pageStatsSynthesis.rights.DD');
      case 'DM':
        return t('pageStatsSynthesis.rights.DM');
      case 'DC':
        return t('pageStatsSynthesis.rights.DC');
      default:
        return level;
    }
  };
  const rightsItems = (data.users_by_rights || []).map((it) => ({
    label: _rightsLabel(it.label),
    count: it.count,
    _level: it.label,
  })) as Array<{ label: string; count: number; _level: string }>;
  _renderDonutColored('ssyn-by-rights', rightsItems, (item) =>
    _rightsColor((item as { _level?: string })._level || item.label),
  );

  _renderCurrentRevision(data.current_revision, ref);

  const matrixEl = document.getElementById('ssyn-matrix');
  if (matrixEl) {
    if (pid && pid !== 'none') {
      const safePid = parseInt(pid, 10);
      if (Number.isFinite(safePid)) {
        matrixEl.innerHTML = `<iframe src="/#matrix/${safePid}" style="width:100%;height:520px;border:0;background:var(--bg)"></iframe>`;
      } else {
        matrixEl.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noProjectSelected'))}</div>`;
      }
    } else {
      matrixEl.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noProjectSelected'))}</div>`;
    }
  }

  _renderStackedAgeChart('ssyn-by-status-and-age', data.actions_by_status_and_age, ref);

  const teamEl = document.getElementById('ssyn-by-team');
  if (!teamEl) return;
  const teams = data.users_by_team || [];
  if (!teams.length) {
    teamEl.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noData'))}</div>`;
  } else {
    makeChart(teamEl, {
      type: 'bar',
      data: {
        labels: teams.map((team) => team.label),
        datasets: [
          {
            label: t('pageStatsSynthesis.dsUsers'),
            data: teams.map((team) => team.count),
            backgroundColor: chartColors(teams.length),
          },
        ],
      },
      options: {
        ...chartCommonOptions(),
        plugins: {
          legend: { display: false },
          datalabels: { color: '#fff', font: { weight: 'bold', size: 11 } },
        },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
      plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
    });
  }
}

function _renderStackedAgeChart(
  containerId: string,
  payload: StackedAgePayload | undefined,
  ref: StatsRefData,
): void {
  const el = document.getElementById(containerId);
  if (!el) return;
  if (!payload || !payload.statuses || !payload.statuses.length) {
    el.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noData'))}</div>`;
    return;
  }
  const fallback = chartColors(payload.statuses.length);
  const datasets = payload.statuses.map((s, i) => ({
    label: s.name,
    data: s.counts,
    backgroundColor: _colorForStatus(s.name, ref) || fallback[i],
    stack: 'age',
  }));

  makeChart(el, {
    type: 'bar',
    data: { labels: payload.buckets, datasets },
    options: {
      ...chartCommonOptions(),
      plugins: {
        legend: { position: 'right', labels: { font: { size: 11 } } },
        tooltip: { backgroundColor: 'rgba(0,0,0,0.85)', padding: 8 },
        datalabels: {
          color: '#fff',
          font: { weight: 'bold', size: 10 },
          formatter: (v: number) => (v > 0 ? v : ''),
        },
      },
      scales: {
        x: { stacked: true, ticks: { font: { size: 11 } } },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    },
    plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
  });
}

interface ChartCenterCtx {
  ctx: CanvasRenderingContext2D;
  chartArea: { left: number; right: number; top: number; bottom: number } | null;
}

function _renderRaciQualityChart(containerId: string, data: ProjectSynthesisData): void {
  const el = document.getElementById(containerId);
  if (!el) return;
  const o = data.actions_with_owner || {};
  const a = data.actions_with_individual_actor || {};
  const total = (o.with_owner || 0) + (o.without_owner || 0);
  if (!total) {
    el.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noActions'))}</div>`;
    return;
  }
  const OK = '#1D9E75';
  const KO = '#dc2626';
  const centerTextPlugin = {
    id: 'centerText_' + containerId,
    afterDatasetsDraw(chart: ChartCenterCtx) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const cx = (chartArea.left + chartArea.right) / 2;
      const cy = (chartArea.top + chartArea.bottom) / 2;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#1A2540';
      ctx.font = 'bold 28px Inter, system-ui, sans-serif';
      ctx.fillText(String(total), cx, cy - 4);
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#646E7E';
      ctx.fillText(t('pageStatsSynthesis.donutTotal'), cx, cy + 14);
      ctx.restore();
    },
  };
  makeChart(el, {
    type: 'doughnut',
    data: {
      labels: [t('pageStatsSynthesis.lblWith'), t('pageStatsSynthesis.lblWithout')],
      datasets: [
        {
          label: t('pageStatsSynthesis.lblOwner'),
          data: [o.with_owner || 0, o.without_owner || 0],
          backgroundColor: [OK, KO],
          borderWidth: 1,
        },
        {
          label: t('pageStatsSynthesis.lblIndividualActor'),
          data: [a.with_individual || 0, a.without_individual || 0],
          backgroundColor: [OK, KO],
          borderWidth: 1,
        },
      ],
    },
    options: {
      ...chartCommonOptions(),
      cutout: '50%',
      plugins: {
        ...(chartCommonOptions() as { plugins?: Record<string, unknown> }).plugins,
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: {
              dataset: { label?: string };
              chart: { data: { labels: string[] } };
              dataIndex: number;
              parsed: number;
            }) => {
              const ds = ctx.dataset.label || '';
              const lbl = ctx.chart.data.labels[ctx.dataIndex];
              return `${ds} — ${lbl} : ${ctx.parsed}`;
            },
          },
        },
        datalabels: {
          color: '#fff',
          font: { weight: 'bold', size: 11 },
          formatter: (
            value: number,
            ctx: {
              dataset: { label?: string };
              chart: { data: { labels: string[] } };
              dataIndex: number;
            },
          ) => {
            if (!value) return '';
            const ds = ctx.dataset.label || '';
            const lbl = ctx.chart.data.labels[ctx.dataIndex];
            return `${ds.charAt(0)} ${lbl}: ${value}`;
          },
        },
      },
    },
    plugins: [
      centerTextPlugin,
      ...(typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : []),
    ],
  });
}

function _renderDonutColored(
  containerId: string,
  items: ReadonlyArray<DonutItem> | undefined,
  colorFn?: (item: DonutItem) => string | null,
): void {
  const el = document.getElementById(containerId);
  if (!el) return;
  if (!items || !items.length) {
    el.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:8px">${esc(t('pageStatsSynthesis.noData'))}</div>`;
    return;
  }
  const fallback = chartColors(items.length);
  const colors = items.map((it, i) => (colorFn && colorFn(it)) || fallback[i]);
  const total = items.reduce((s, it) => s + (it.count || 0), 0);

  const centerTextPlugin = {
    id: 'centerText_' + containerId,
    afterDatasetDraw(chart: ChartCenterCtx) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const cx = (chartArea.left + chartArea.right) / 2;
      const cy = (chartArea.top + chartArea.bottom) / 2;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#1A2540';
      ctx.font = 'bold 28px Inter, system-ui, sans-serif';
      ctx.fillText(String(total), cx, cy - 4);
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#646E7E';
      ctx.fillText(t('pageStatsSynthesis.donutTotal'), cx, cy + 14);
      ctx.restore();
    },
  };

  makeChart(el, {
    type: 'doughnut',
    data: {
      labels: items.map((i) => i.label),
      datasets: [{ data: items.map((i) => i.count), backgroundColor: colors }],
    },
    options: {
      ...chartCommonOptions(),
      cutout: '62%',
      plugins: {
        ...(chartCommonOptions() as { plugins?: Record<string, unknown> }).plugins,
        datalabels: {
          color: '#fff',
          font: { weight: 'bold', size: 11 },
          formatter: (
            value: number,
            ctx: { chart: { data: { labels: string[] } }; dataIndex: number },
          ) => {
            if (!value) return '';
            const label = ctx.chart.data.labels[ctx.dataIndex];
            return `${label}: ${value}`;
          },
        },
      },
    },
    plugins: [
      centerTextPlugin,
      ...(typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : []),
    ],
  });
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window._loadStatsRefData = _loadStatsRefData;
window._SEVERITY_REPRESENTATIVE = _SEVERITY_REPRESENTATIVE;
window._colorForStatus = _colorForStatus;
window._colorForSeverityBin = _colorForSeverityBin;
window._loadProjectSynthesis = _loadProjectSynthesis;
window._renderStackedAgeChart = _renderStackedAgeChart;
window._renderRaciQualityChart = _renderRaciQualityChart;
window._renderDonutColored = _renderDonutColored;

export {};
